import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getProductionJobs } from '../services/api';
import { STAGES } from '../components/prodmgmt/stages';
import NewJobDialog from '../components/prodmgmt/NewJobDialog';
import JobDetailDialog from '../components/prodmgmt/JobDetailDialog';
import DispatchSheet from '../components/prodmgmt/DispatchSheet';
import BottleneckReport from '../components/prodmgmt/BottleneckReport';
import BOMLibrary from '../components/prodmgmt/BOMLibrary';
import { toast } from 'sonner';
import { Factory, Plus, Layers, CheckCircle2, Boxes, LayoutGrid, CalendarDays, BarChart3 } from 'lucide-react';

const DISPATCH_FILTERS = [
  { key: '', label: 'All Dispatches' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'saturday', label: 'Saturday' },
];

const COLUMNS = [...STAGES, { key: '__done__', label: 'Completed' }];

const ProductionPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dispatch, setDispatch] = useState('');
  const [view, setView] = useState('board');
  const [newOpen, setNewOpen] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProductionJobs(dispatch || undefined);
      setJobs(res.data);
    } catch (e) {
      toast.error('Failed to load production jobs');
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const openJob = (id) => { setActiveJobId(id); setDetailOpen(true); };

  // Group jobs into columns
  const columns = {};
  COLUMNS.forEach((c) => { columns[c.key] = []; });
  jobs.forEach((j) => {
    const key = j.status === 'completed' ? '__done__' : j.current_stage;
    if (columns[key]) columns[key].push(j);
    else columns['__done__'].push(j);
  });

  const inProgress = jobs.filter((j) => j.status !== 'completed').length;
  const completed = jobs.filter((j) => j.status === 'completed').length;
  const totalUnits = jobs.reduce((s, j) => s + (Number(j.units) || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="production-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Factory size={24} className="text-accent" />Production Management</h1>
          <p className="text-muted-foreground">Job work planner for aluminium doors & windows — one lane per production stage</p>
        </div>
        <Button className="btn-accent" onClick={() => setNewOpen(true)} data-testid="new-job-btn"><Plus size={16} className="mr-1" />New Job</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-blue-50 flex items-center justify-center"><Layers size={20} className="text-blue-600" /></div><div><div className="text-2xl font-bold">{jobs.length}</div><div className="text-sm text-muted-foreground">Total Jobs</div></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-amber-50 flex items-center justify-center"><Factory size={20} className="text-amber-600" /></div><div><div className="text-2xl font-bold">{inProgress}</div><div className="text-sm text-muted-foreground">In Progress</div></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-green-50 flex items-center justify-center"><CheckCircle2 size={20} className="text-green-600" /></div><div><div className="text-2xl font-bold">{completed}</div><div className="text-sm text-muted-foreground">Completed</div></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-sm bg-purple-50 flex items-center justify-center"><Boxes size={20} className="text-purple-600" /></div><div><div className="text-2xl font-bold">{totalUnits}</div><div className="text-sm text-muted-foreground">Total Units</div></div></CardContent></Card>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1">
          {DISPATCH_FILTERS.map((f) => (
            <Button key={f.key || 'all'} size="sm" variant={dispatch === f.key ? 'default' : 'outline'} onClick={() => setDispatch(f.key)} data-testid={`dispatch-filter-${f.key || 'all'}`}>{f.label}</Button>
          ))}
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant={view === 'board' ? 'default' : 'outline'} onClick={() => setView('board')} data-testid="view-board"><LayoutGrid size={14} className="mr-1" />Planner Board</Button>
          <Button size="sm" variant={view === 'dispatch' ? 'default' : 'outline'} onClick={() => setView('dispatch')} data-testid="view-dispatch"><CalendarDays size={14} className="mr-1" />Dispatch Sheet</Button>
          <Button size="sm" variant={view === 'bottleneck' ? 'default' : 'outline'} onClick={() => setView('bottleneck')} data-testid="view-bottleneck"><BarChart3 size={14} className="mr-1" />Bottlenecks</Button>
          <Button size="sm" variant={view === 'bom' ? 'default' : 'outline'} onClick={() => setView('bom')} data-testid="view-bom"><Layers size={14} className="mr-1" />BOM Library</Button>
        </div>
      </div>

      {view === 'bom' ? (
        <BOMLibrary />
      ) : view === 'bottleneck' ? (
        <BottleneckReport />
      ) : loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" /></div>
      ) : jobs.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center justify-center h-48 text-muted-foreground"><Factory size={40} className="mb-3 opacity-50" /><p>No production jobs yet</p><p className="text-sm mt-1">Create a job from a work order to start the planner</p></CardContent></Card>
      ) : view === 'dispatch' ? (
        <DispatchSheet jobs={jobs} onOpenJob={openJob} />
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3" style={{ minWidth: 'max-content' }}>
            {COLUMNS.map((col) => {
              const colJobs = columns[col.key] || [];
              const isDone = col.key === '__done__';
              return (
                <div key={col.key} className="w-64 flex-shrink-0" data-testid={`lane-${col.key}`}>
                  <div className={`px-3 py-2 rounded-t-md text-sm font-medium flex items-center justify-between ${isDone ? 'bg-green-600 text-white' : 'bg-secondary text-secondary-foreground'}`}>
                    <span>{col.label}</span>
                    <span className="text-xs bg-white/20 rounded-full px-2">{colJobs.length}</span>
                  </div>
                  <div className="bg-muted/30 rounded-b-md p-2 space-y-2 min-h-[120px]">
                    {colJobs.map((j) => (
                      <button key={j.id} onClick={() => openJob(j.id)} className="w-full text-left bg-card border rounded-md p-3 hover:border-accent transition-colors shadow-sm" data-testid={`job-card-${j.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm font-medium">{j.job_number}</span>
                          {j.dispatch_day && <span className="text-[10px] uppercase tracking-wide text-blue-600">{j.dispatch_day.slice(0, 3)}</span>}
                        </div>
                        <p className="text-sm truncate mt-0.5">{j.customer}</p>
                        <p className="text-xs text-muted-foreground mt-1">{j.units} units · {(j.items || []).length} items</p>
                      </button>
                    ))}
                    {colJobs.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">—</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <NewJobDialog open={newOpen} onOpenChange={setNewOpen} onCreated={fetchJobs} />
      <JobDetailDialog jobId={activeJobId} open={detailOpen} onOpenChange={setDetailOpen} onChanged={fetchJobs} />
    </div>
  );
};

export default ProductionPage;
